import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";

import { merge } from "lodash";

import { useAppSelector } from "app/hooks";
import InputFormField from "components/input-form-field";
import ModalForm from "components/modal-form";
import SelectFormField from "components/select-form-field";
import { US_COUNTRY_VALUE } from "constants/addresses";
import TITLES from "constants/titles";
import {
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentUser
} from "features/auth/auth-slice";
import { PhysicianFormValues } from "features/settings/physicians/physician-form.component";
import NPISearchModal from "features/settings/physicians/npi-search-modal.component";
import { useAddPhysicianMutation } from "services/physicians";
import { Physician, NPPESSearchResult } from "services/types/physician.type";
import { EMAIL_REGEX } from "utils/validations.utils";

import { PhoneField } from "../../features/patients/patient-form/fields";
import FaxNumberField from "../fax-form/fax-number-field.component";

export const EMPTY_DEFAULT_VALUES: PhysicianFormValues = {
  first_name: "",
  last_name: "",
  title: "",
  suffix: "",
  is_active: true,
  npi: "",
  phone: "",
  fax_phone: "",
  email: "",
  street_address_1: "",
  street_address_2: "",
  city: "",
  state: "",
  zip_code: "",
  notes: "",
  country: US_COUNTRY_VALUE,
  practice_name: ""
};

type PhysicianShortFormProps = {
  isOpen: boolean;
  newPhysician?: Partial<PhysicianFormValues>;
  onSave: (data: Physician) => void;
  onCancel: () => void;
};

export default function PhysicianShortForm({
  isOpen,
  newPhysician = {},
  onSave,
  onCancel
}: PhysicianShortFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [npiSearchOpen, setNpiSearchOpen] = useState(false);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const currentUser = useAppSelector(selectCurrentUser);

  const defaultValues: PhysicianFormValues = useMemo(() => {
    return merge(
      {},
      {
        ...EMPTY_DEFAULT_VALUES,
        country:
          currentClinic?.country ||
          currentPractice?.country ||
          EMPTY_DEFAULT_VALUES.country
      },
      newPhysician
    );
  }, [newPhysician, currentPractice?.country, currentClinic?.country]);

  const formMethods = useForm<PhysicianFormValues>({
    mode: "onChange",
    defaultValues
  });

  const { control, reset, handleSubmit, setValue, trigger } = formMethods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const [createPhysician] = useAddPhysicianMutation();

  const onNpiSelect = useCallback(
    async (result: NPPESSearchResult) => {
      const opts = { shouldValidate: true, shouldDirty: true } as const;
      setValue("first_name", result.first_name || "", opts);
      setValue("last_name", result.last_name || "", opts);
      setValue("suffix", result.suffix || "", opts);
      setValue("npi", result.npi || "", opts);
      setValue("phone", result.phone || "", opts);
      setValue("fax_phone", result.fax_phone || "", opts);
      await trigger();
    },
    [setValue, trigger]
  );

  return (
    <>
    <ModalForm
      isOpen={isOpen}
      isSubmitting={isSubmitting}
      onCancel={() => {
        reset();
        onCancel();
      }}
      onSubmit={handleSubmit((data) => {
        setIsSubmitting(true);

        createPhysician({
          ...data,
          title: data.title || null,
          phone: data.phone.replace(/\D/g, ""),
          fax_phone: data.fax_phone.replace(/\D/g, ""),
          state: String(currentClinic?.state || currentUser?.default_clinic?.state || "")
        }).then((response) => {
          if ("data" in response) {
            onSave(response.data);
            reset();
          }
          setIsSubmitting(false);
        });
      })}
      title="New Physician Quick Add"
      hideBackdrop={false}
      formName="physician"
      control={control}
      maxWidth="lg"
    >
      <Stack sx={{ pb: 2 }} spacing={2}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setNpiSearchOpen(true)}
          sx={{ alignSelf: "flex-start" }}
        >
          Search NPI Registry
        </Button>
        <InputFormField
          name="first_name"
          label="First Name"
          control={control}
          required
        />
        <InputFormField
          name="last_name"
          label="Last Name"
          control={control}
          required
        />
        <Grid item container xs={12} justifyContent="space-between">
          <Grid item xs={3}>
            <SelectFormField
              name="title"
              label="Title"
              control={control}
              options={TITLES.getOptions()}
            />
          </Grid>
          <Grid item xs={8}>
            <InputFormField
              name="suffix"
              label="Suffix"
              control={control}
              sx={null}
            />
          </Grid>
        </Grid>

        <InputFormField name="npi" label="NPI" control={control} />

        <Grid item container xs={12} justifyContent="space-between">
          <Grid item xs={6}>
            <PhoneField name="phone" control={control} sx={{ ml: 2, mt: 1 }} />
          </Grid>
          <Grid item xs={5}>
            <FaxNumberField
              name="fax_phone"
              label="Fax number"
              control={control}
            />
          </Grid>
        </Grid>

        <InputFormField
          name="email"
          label="Email"
          control={control}
          rules={{
            pattern: {
              value: EMAIL_REGEX,
              message: "Email is not valid."
            }
          }}
          sx={null}
        />
      </Stack>
    </ModalForm>
    <NPISearchModal
      open={npiSearchOpen}
      onClose={() => setNpiSearchOpen(false)}
      onSelect={onNpiSelect}
    />
    </>
  );
}
